import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, useRoleLabel } from "../lib/auth";
import { Bell, Menu, CircleAlert } from "lucide-react";
import api from "../lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import LanguageSwitcher from "./LanguageSwitcher";

const OPS_ROLES = ["corporate_admin", "treasury", "branch_manager", "operativo", "hr_admin", "colaborador", "mantenimiento", "direccion"];

export default function Topbar({ title, subtitle, onMobileMenu }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const roleLabel = useRoleLabel();
  const [summary, setSummary] = useState({ critical: 0, warning: 0, info: 0, total: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    const promises = [api.get("/alerts/summary").then(r => r.data).catch(() => ({ critical: 0, warning: 0, info: 0, total: 0 }))];
    // Fix WH78 (2026-10-15): Legal no entra a Ops, pero recibe los avisos de
    // vigencias (marcas) como alerta de Ops.
    if (OPS_ROLES.includes(user?.role) || user?.role === "legal") {
      promises.push(
        api.get("/ops/alerts/me").then(r => {
          const alerts = r.data?.alerts || [];
          const unread = alerts.filter(a => !(a.read_by || []).some(rb => rb.user_id === user.id));
          return {
            critical: unread.filter(a => a.severity === "critical").length,
            warning: unread.filter(a => a.severity === "warning").length,
            info: unread.filter(a => a.severity === "info").length,
            total: unread.length,
          };
        }).catch(() => ({ critical: 0, warning: 0, info: 0, total: 0 }))
      );
    }
    Promise.all(promises).then((results) => {
      if (!mounted) return;
      const merged = results.reduce((acc, r) => ({
        critical: acc.critical + (r.critical || 0),
        warning: acc.warning + (r.warning || 0),
        info: acc.info + (r.info || 0),
        total: acc.total + (r.total || 0),
      }), { critical: 0, warning: 0, info: 0, total: 0 });
      setSummary(merged);
    });
    return () => { mounted = false; };
  }, [location.pathname, user?.role, user?.id]);

  const hasCritical = summary.critical > 0;
  const hasWarning = !hasCritical && summary.warning > 0;
  const totalUnread = summary.total;

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        {onMobileMenu && (
          <button
            data-testid="mobile-menu-btn"
            onClick={onMobileMenu}
            className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        <div>
          {title && (
            <h1 className="font-display font-extrabold text-xl text-[#1A1A1A] tracking-tight leading-none">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <LanguageSwitcher variant="topbar" />
        <motion.button
          data-testid="topbar-alerts-btn"
          onClick={() => navigate("/alertas")}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
            hasCritical ? "bg-red-50 hover:bg-red-100 ring-2 ring-red-200" :
            hasWarning ? "bg-amber-50 hover:bg-amber-100 ring-2 ring-amber-200" :
            "hover:bg-slate-100"
          }`}
          title={
            hasCritical
              ? t("topbar.criticalAlerts", { count: summary.critical })
              : hasWarning
                ? t("topbar.warningAlerts", { count: summary.warning })
                : t("topbar.alertsTitle")
          }
        >
          {hasCritical ? (
            <CircleAlert className="w-5 h-5 text-red-600" strokeWidth={2.5} />
          ) : (
            <Bell className={`w-5 h-5 ${hasWarning ? "text-amber-700" : "text-slate-700"}`} strokeWidth={2.2} />
          )}
          {totalUnread > 0 && (
            <span
              data-testid="alert-count-badge"
              className={`absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center text-white ${
                hasCritical ? "bg-red-600" : hasWarning ? "bg-amber-500" : "bg-slate-600"
              }`}
            >
              {totalUnread}
            </span>
          )}
          {hasCritical && (
            <span className="absolute inset-0 rounded-xl ring-2 ring-red-500/50 animate-ping" />
          )}
        </motion.button>

        {/* Mini severity legend - testids always rendered for QA */}
        <div className={`hidden xl:flex items-center gap-2 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg ${totalUnread === 0 ? "opacity-50" : ""}`}>
          <span
            data-testid="alert-summary-critical"
            className={`flex items-center gap-1 text-[11px] font-bold text-red-700 ${summary.critical === 0 ? "hidden" : ""}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {t("topbar.alertCritical", { count: summary.critical })}
          </span>
          <span
            data-testid="alert-summary-warning"
            className={`flex items-center gap-1 text-[11px] font-bold text-amber-700 ${summary.warning === 0 ? "hidden" : ""}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {t("topbar.alertWarning", { count: summary.warning })}
          </span>
          <span
            data-testid="alert-summary-info"
            className={`flex items-center gap-1 text-[11px] font-bold text-blue-700 ${summary.info === 0 ? "hidden" : ""}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {t("topbar.alertInfo", { count: summary.info })}
          </span>
          {totalUnread === 0 && (
            <span className="text-[11px] text-slate-400 font-semibold">{t("topbar.allUpToDate")}</span>
          )}
        </div>

        <div className="flex items-center gap-2.5 pl-3 ml-1 border-l border-slate-200">
          <div className="w-9 h-9 rounded-full bg-[#FFDF00] flex items-center justify-center font-bold text-sm text-[#1A1A1A]">
            {(user?.full_name || "?").charAt(0)}
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="text-sm font-semibold text-[#1A1A1A]">{user?.full_name}</div>
            <div className="text-[11px] text-slate-500">{roleLabel(user?.role)}</div>
          </div>
        </div>
      </div>
    </header>
  );
}
